import { ChangeEvent } from "react";
import { StringMap, Currencies, CountryCurrencies, NameIdSub } from "./types/types.js";

const febDays = (year: number) =>
  (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0 ? 29 : 28;

export const monthToDay = (year: number): { [key: number]: number } => ({
  1: 31,
  2: febDays(year),
  3: 31,
  4: 30,
  5: 31,
  6: 30,
  7: 31,
  8: 31,
  9: 30,
  10: 31,
  11: 30,
  12: 31,
});

export const monthName = (month: number | string) => {
  return [
    trns("IDS_MONTH_JAN_SHORT", "Jan"),
    trns("IDS_MONTH_FEB_SHORT", "Feb"),
    trns("IDS_MONTH_MAR_SHORT", "Mar"),
    trns("IDS_MONTH_APR_SHORT", "Apr"),
    trns("IDS_MONTH_MAY_SHORT", "May"),
    trns("IDS_MONTH_JUN_SHORT", "Jun"),
    trns("IDS_MONTH_JUL_SHORT", "Jul"),
    trns("IDS_MONTH_AUG_SHORT", "Aug"),
    trns("IDS_MONTH_SEP_SHORT", "Sep"),
    trns("IDS_MONTH_OCT_SHORT", "Oct"),
    trns("IDS_MONTH_NOV_SHORT", "Nov"),
    trns("IDS_MONTH_DEC_SHORT", "Dec"),
  ][Number(month) - 1];
};

export const mergeProvinces = (provinces: NameIdSub[]): StringMap<NameIdSub[]> =>
  provinces.reduce((acc: StringMap<NameIdSub[]>, a: NameIdSub) => {
    //Add to
    if (a.sub_id && a.sub_id in acc) {
      return { ...acc, [a.sub_id]: [...acc[a.sub_id], { name: a.name, id: a.id }] };
    }

    //Initiate
    if (a.sub_id) {
      return { ...acc, [a.sub_id]: [{ name: a.name, id: a.id }] };
    }

    return acc;
  }, {});

export const mergeCurrencies = (currencies: Currencies[]): StringMap<Currencies> =>
  currencies.reduce(
    (acc: StringMap<Currencies>, a: Currencies) => ({ ...acc, [a.currency_id]: a }),
    {}
  );

export const mergeCountryCur = (
  country_currencies: CountryCurrencies[],
  currencies: StringMap<Currencies>
) => {
  return country_currencies.reduce((acc: StringMap<CountryCurrencies>, a: any) => {
    if (a.id in acc) {
      return { ...acc, [a.id]: { ...acc[a.id], name: currencies[a.name] } };
    } else {
      return { ...acc, [a.id]: { name: currencies[a.name] } };
    }
  }, {});
};

export const mergeRegions = (regions: NameIdSub[]) =>
  regions.reduce(
    (acc: StringMap<NameIdSub>, a: any) => ({
      ...acc,
      [a.id]: { name: a.name, sub_id: a.sub_id },
    }),
    {}
  );

export const getFields = (step: StringMap<any>, req: StringMap<boolean>, opt: StringMap<boolean>) =>
  Object.keys(step).reduce((acc: StringMap<any>, b: string) => {
    if (req[b] || opt[b]) {
      return { ...acc, [b]: (step[b] !== "" ? step[b] : null) };
    } else {
      return { ...acc };
    }
  }, {})

export const trns = (id: string | number, backup: string) => {
  if (!window.trans || Object.keys(window.trans).length === 0) {
    // console.log('Translation object is empty, possible race condition. Returning backup string.');
    return backup;
  }

  if (window.trans[id]) return window.trans[id];

  // console.log(`Translation for id: ${id} not found, returning backup string.`);
  return backup;
};

export const toTitleCase = (str: string) =>
  str
    .toLowerCase()
    .split(" ")
    .map(word => word.replace(word.charAt(0), word.charAt(0).toUpperCase()))
    .join(" ");

export const capitalize = (id: string, backup: string) => {
  const word = trns(id, backup).toLowerCase();

  return word.charAt(0).toUpperCase() + word.slice(1);
};

// start: incl, stop: excl
// range(0,5) -> [0,1,2,3,4]
// range(-5, 0) -> [-5, -4, -3, -2, -1]
// range(10, 5) -> [10,9,8,7,6]
export const range = (start: number, stop: number) => {
  let res = [];
  while (start !== stop) {
    res.push(start);
    start < stop ? start++ : start--;
  }
  return res;
};

export const lowchar = (str: string) => {
  if (str.length === 0) return null;
  let min: number = Infinity;
  for (let i = 0; i < str.length; i++) {
    min = Math.min(min, str.charCodeAt(i));
  }
  return min;
};

export const arrayEquals = (arr1: any[], arr2: any[]) => {
  return (
    Array.isArray(arr1.sort()) &&
    Array.isArray(arr2.sort()) &&
    arr1.length === arr2.length &&
    arr1.every((val, index) => val === arr2[index])
  );
};

export const removeSpaces = (str: string) => {
  const newStr = str.replace(/\s+/g, "").toLowerCase();
  return newStr;
};

export const insertAfter = (referenceNode: HTMLElement, newNode: HTMLElement) => {
  referenceNode.parentNode &&
    referenceNode.parentNode.insertBefore(newNode, referenceNode.nextSibling);
};

export const constructStepParams = (acc: StringMap<boolean>, key: string) => {
  return key === "birth_date"
    ? { ...acc, birth_date: true, year: true, month: true, day: true }
    : { ...acc, [key]: true };
};

export const sanitizeInput = (input: string) => input
  .replace(/[^a-zA-Z0-9 ]/g, "") // Remove invalid characters
  .replace(/\s+/g, " "); // Replace multiple spaces with a single space;

export const filterString = (e: ChangeEvent<HTMLInputElement>) => {
  if ((e.nativeEvent as InputEvent)?.isComposing) return e;
  return { ...e, target: { ...e.target, value: sanitizeInput(e.target.value) } }
}

export const sanitizeName = (input: string) => input
  .normalize("NFC")
  .replace(/[’ʼ]/gu, "'")
  .replace(/[^\p{L}\p{M}\p{Pd} '.]+/gu, " ") // Replace runs of invalid characters with a single space
  .replace(/\s+/gu, " ") // Collapse whitespace
  .trim();

export const filterName = (e: ChangeEvent<HTMLInputElement>) => {
  // Skip sanitizing mid-IME-composition (e.g. pinyin/romaji/hangul, dead-key accents) so the
  // in-progress preedit text isn't mangled; the committed value is sanitized once composition ends.
  if ((e.nativeEvent as InputEvent)?.isComposing) return e;
  return { ...e, target: { ...e.target, value: sanitizeName(e.target.value) } }
}